import { ChevronDown, LoaderCircle, LogOut, UserPen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useSession } from "@/context/SessionContext";
import { logoutSession } from "@/lib/api";
import {
  getNavigationRole,
  getNavigationTabs,
  isNavigationTabActive
} from "@/lib/navigation";

const gupioLogo = "/branding/gupio-logo.png";

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearSession, session } = useSession();
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const navigationRole = getNavigationRole(session);
  const navigationTabs = getNavigationTabs(navigationRole);
  const showGuestGetStarted = !session && location.pathname === "/";

  useEffect(() => {
    if (!isProfileMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProfileMenuOpen]);

  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    setIsLoggingOut(true);
    setIsProfileMenuOpen(false);

    try {
      await logoutSession();
    } catch {
      // Clear the local session even if the logout request fails so the browser state is reset.
    } finally {
      setIsLogoutDialogOpen(false);
      setIsLoggingOut(false);
      clearSession();
      navigate("/");
    }
  }

  const profileName =
    session?.role === "admin" ? session.user.name : session?.role === "candidate" ? session.user.fullName : "";
  const profileInitials = getProfileInitials(profileName);

  return (
    <header className="sticky top-0 z-40 border-b border-line/90 bg-white/95 backdrop-blur-md">
      <div className="px-4 py-3 sm:px-6 sm:py-4 lg:px-8">
        <div className="flex items-center justify-between gap-3 xl:gap-6">
          <div className="min-w-0 flex flex-1 items-center gap-3 sm:gap-4 lg:gap-5">
            <Link className="flex min-w-0 items-center gap-3 text-navy sm:gap-4" to="/">
              <img
                alt="Gupio"
                className="h-8 w-auto shrink-0 object-contain sm:h-10 md:h-12"
                src={gupioLogo}
              />
              <div className="hidden border-l border-line/80 pl-4 sm:block">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate">
                  Legal Agreement Portal
                </p>
                <p className="mt-1 text-sm text-slate/80">Secure onboarding and agreements</p>
              </div>
            </Link>

            {session ? (
              <div className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate/70 lg:flex">
                <span className="rounded-full border border-line/70 bg-cloud/80 px-3 py-2 text-royal">
                  {session.role === "admin" ? "Admin Panel" : "Candidate Workspace"}
                </span>
              </div>
            ) : null}
          </div>

          <div className="relative flex shrink-0 items-center gap-2 sm:gap-3" ref={profileMenuRef}>
            {session ? (
              <>
                <button
                  aria-expanded={isProfileMenuOpen}
                  aria-haspopup="menu"
                  aria-label="Open profile menu"
                  className="inline-flex items-center gap-2 rounded-full border border-line/80 bg-white px-1.5 py-1.5 text-slate shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_28px_rgba(17,24,39,0.08)] focus:outline-none focus-visible:ring-4 focus-visible:ring-royal/10"
                  data-testid="navbar-profile-menu-button"
                  onClick={() => setIsProfileMenuOpen((current) => !current)}
                  type="button"
                >
                  <Avatar className="h-10 w-10 border border-line/80 bg-white sm:h-11 sm:w-11">
                    <AvatarFallback>{profileInitials}</AvatarFallback>
                  </Avatar>
                  <ChevronDown
                    className={`hidden h-4 w-4 text-slate transition sm:block ${
                      isProfileMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isProfileMenuOpen ? (
                  <div
                    className="absolute right-0 top-full z-40 mt-3 w-[min(88vw,240px)] rounded-[24px] border border-line/80 bg-white p-2 shadow-[0_24px_60px_rgba(15,23,42,0.14)]"
                    data-testid="navbar-profile-menu"
                    role="menu"
                  >
                    <div className="rounded-[18px] bg-[linear-gradient(180deg,#f8fbff_0%,#eef4ff_100%)] px-4 py-3">
                      <p className="text-sm font-semibold text-ink">{profileName || "Profile"}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate/70">
                        {session.role === "admin" ? "Admin access" : "Candidate workspace"}
                      </p>
                    </div>

                    <div className="mt-2 grid gap-1">
                      <Link
                        className="inline-flex items-center gap-3 rounded-[18px] px-4 py-3 text-sm font-semibold text-slate transition hover:bg-cloud hover:text-navy"
                        data-testid="navbar-profile-link"
                        onClick={() => setIsProfileMenuOpen(false)}
                        role="menuitem"
                        to="/profile"
                      >
                        <UserPen className="h-4 w-4" />
                        Edit profile
                      </Link>

                      <button
                        className="inline-flex items-center gap-3 rounded-[18px] px-4 py-3 text-left text-sm font-semibold text-rose-700 transition hover:bg-rose-50"
                        data-testid="navbar-logout-button"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          setIsLogoutDialogOpen(true);
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <LogOut className="h-4 w-4" />
                        Log out
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : showGuestGetStarted ? (
              <Link className="btn-primary px-4 py-2.5" to="/auth?role=candidate">
                Get Started
              </Link>
            ) : null}
          </div>
        </div>

        {navigationTabs.length > 0 ? (
          <div className="mt-4 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <nav className="inline-flex min-w-full items-center gap-2 rounded-[18px] border border-line bg-white p-2 shadow-[0_12px_28px_rgba(17,24,39,0.06)]">
              {navigationTabs.map((tab) => {
                const isActive = isNavigationTabActive(tab, location);

                return (
                  <Link
                    aria-current={isActive ? "page" : undefined}
                    className={`inline-flex shrink-0 items-center rounded-[12px] px-4 py-3 text-sm font-semibold transition duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-royal/10 ${
                      isActive
                        ? "bg-navy text-white shadow-[0_12px_24px_rgba(23,37,84,0.18)]"
                        : "text-slate hover:bg-cloud hover:text-navy"
                    }`}
                    key={tab.id}
                    to={tab.to}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        ) : null}
      </div>

      <AlertDialog
        onOpenChange={(open) => {
          if (!isLoggingOut) {
            setIsLogoutDialogOpen(open);
          }
        }}
        open={isLogoutDialogOpen}
      >
        <AlertDialogContent data-testid="navbar-logout-dialog">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-600">
            <LogOut className="h-6 w-6" />
          </div>

          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to log out?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be signed out of the legal agreement portal. If you have unsaved changes in
              this session, they may be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="rounded-2xl border border-rose-100 bg-rose-50/80 px-4 py-3">
            <p className="text-sm leading-6 text-rose-800">
              Please save your work before continuing if you still need to review or complete the
              agreement.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              className="btn-secondary px-4 py-2.5"
              data-testid="navbar-logout-cancel"
              disabled={isLoggingOut}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="navbar-logout-confirm"
              disabled={isLoggingOut}
              onClick={(event) => {
                event.preventDefault();
                void handleLogout();
              }}
            >
              {isLoggingOut ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}

function getProfileInitials(value: string) {
  const parts = value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) {
    return "GU";
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("");
}
